import { PERMISSIONS } from '@repo/contracts';
import { lazy } from 'react';
import { Navigate, type RouteObject, useLocation } from 'react-router';

import { AppShell } from '@/app/app-shell';
import { APP_ROUTES } from '@/constants/app-routes';

import { RequireAuth } from './require-auth';
import { RequirePermission } from './require-permission';

const LoginPage = lazy(() => import('@/pages/login/LoginPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/NotFoundPage'));
const RouteErrorPage = lazy(() => import('@/pages/route-error/RouteErrorPage'));
const JobsPage = lazy(() => import('@/pages/jobs/JobsPage'));
const JobPage = lazy(() => import('@/pages/job/JobPage'));
const RunsPage = lazy(() => import('@/pages/runs/RunsPage'));
const RunPage = lazy(() => import('@/pages/run/RunPage'));
const ConflictsPage = lazy(() => import('@/pages/conflicts/ConflictsPage'));

/** The runs list used to live at /runs; a saved link keeps its filters on the way to the new path. */
function LegacyRunsRedirect() {
  const { search } = useLocation();

  return <Navigate to={`${APP_ROUTES.runs}${search}`} replace />;
}

export const routes: RouteObject[] = [
  { path: APP_ROUTES.login, element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    element: <RequireAuth />,
    // Last resort: only reached if AppShell itself fails to render, so it
    // renders RouteErrorPage without the sidebar chrome that just broke.
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            // A pathless layout route so the boundary sits inside AppShell:
            // an error here keeps the sidebar, instead of errorElement on
            // AppShell itself replacing the whole shell.
            errorElement: <RouteErrorPage />,
            children: [
              {
                element: <RequirePermission permission={PERMISSIONS.directoryRead} />,
                children: [
                  { path: APP_ROUTES.home, element: <JobsPage /> },
                  { path: APP_ROUTES.job(':job'), element: <JobPage /> },
                  { path: APP_ROUTES.runs, element: <RunsPage /> },
                  { path: APP_ROUTES.legacyRuns, element: <LegacyRunsRedirect /> },
                  { path: APP_ROUTES.run(':id'), element: <RunPage /> },
                  { path: APP_ROUTES.conflicts, element: <ConflictsPage /> },
                ],
              },
            ],
          },
          // Last child, so it never shadows a real route above it.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
